import type React from 'react';

import { SearchRounded } from '@mui/icons-material';
import { InputAdornment } from '@mui/material';
import {
  LabelField,
  OrdReferenceField,
  OrdDatagrid,
  OrdList,
  OrdEditInDialogButton,
  OrdIconStandard,
  FiqlSearch,
} from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { AgentSettingsFields } from '@ragu-components/ord-types';
import { TextField, useTranslate, FunctionField, WrapperField, ResourceContextProvider } from 'react-admin';

import { AgentsForm } from './AgentsForm';

import type { OrdListProps } from '@ragu-components/ord-components';
import type { AgentFields } from '@ragu-components/ord-types';

export const AgentNameField = (): React.ReactElement => (
  <FunctionField
    label='resources.agents.fields.name'
    sortable={false}
    render={(record: typeof AgentFields) =>
      record.group !== null ? (
        <OrdReferenceField source='group' reference='groups' />
      ) : record.user !== null ? (
        <OrdReferenceField source='user' reference='users' />
      ) : (
        <></>
      )
    }
  />
);

export const AgentTypeField = (): React.ReactElement => {
  const translate = useTranslate();
  return (
    <FunctionField
      label='resources.agents.type'
      sortable={false}
      render={(record: typeof AgentFields) => {
        return record.group !== null && record.group !== undefined
          ? translate('resources.agents.fields.group')
          : record.user !== null && record.user !== undefined
            ? translate('resources.agents.fields.user')
            : '';
      }}
    />
  );
};

export const EmbeddedAgentsList = ({
  icon = <OrdIconStandard name='agents' />,
  ...props
}: Partial<OrdListProps>): React.ReactElement => {
  const SearchInput = ({ resource }: { resource: string }): React.ReactElement => (
    <FiqlSearch
      hasOwnFormContext={false}
      resource={resource}
      type='fixed'
      autoFocus={false}
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            <SearchRounded />
          </InputAdornment>
        ),
      }}
    />
  );
  return (
    <ResourceContextProvider value={Resources.agents}>
      <OrdList
        icon={icon}
        hasCreate={false}
        hasFilters={false}
        disableAuthentication
        headerChildren={<SearchInput resource={Resources.agents} />}
        sy={{ width: '100%' }}
        showTabTitle={false}
        {...props}
      >
        <OrdDatagrid bulkActionButtons={false} rowClick={false}>
          <WrapperField label='resources.queues.fields.name'>
            <AgentNameField />
          </WrapperField>
          <WrapperField label='resources.queues.fields.type'>
            <AgentTypeField />
          </WrapperField>
          <TextField source={`settings.${AgentSettingsFields.busy_delay_time}`} sortable={false} />
          <TextField source={`settings.${AgentSettingsFields.call_timeout}`} sortable={false} />
          <TextField source={`settings.${AgentSettingsFields.no_answer_delay_time}`} sortable={false} />
          <TextField source={`settings.${AgentSettingsFields.position}`} sortable={false} />
          <TextField source={`settings.${AgentSettingsFields.reject_delay_time}`} sortable={false} />
          <TextField source={`settings.${AgentSettingsFields.wrap_up_time}`} sortable={false} />
          <LabelField />
          <WrapperField label='ra.action.edit'>
            <OrdEditInDialogButton mutationMode='pessimistic'>
              <AgentsForm />
            </OrdEditInDialogButton>
          </WrapperField>
        </OrdDatagrid>
      </OrdList>
    </ResourceContextProvider>
  );
};

export default EmbeddedAgentsList;
